import {Module} from "@/modules";
import {
  Player,
  PlayerContractCareerEndNotification,
  PlayerContractNegotiation,
  PlayerContractTerminatedNotification,
  Team
} from "./entity";
import TeamFactory from "@/modules/team/services/factories/TeamFactory";
import TeamLink from "./components/TeamLink.vue";
import {LinkComponentManager} from "@/modules/vue/services/LinkComponentManager";
import messages from './resources/messages';
import LineupSkillCalculator from "@/modules/team/services/LineupSkillCalculator";
import PlayerLink from "@/modules/team/components/PlayerLink.vue";
import {PlayerProgressEventSubscriber} from "@/modules/team/subscribers/PlayerProgressEventSubscriber";
import MarketValueCalculator from "@/modules/team/services/MarketValueCalculator";
import {GameCreationEventSubscriber} from "@/modules/team/subscribers/GameCreationEventSubscriber";
import {routes} from "./resources/routes";
import {PlayerFormEventSubscriber} from "@/modules/team/subscribers/PlayerFormEventSubscriber";
import EffectiveLineupSkillCalculator from "@/modules/team/services/EffectiveLineupSkillCalculator";
import {PlayerEnergyEventSubscriber} from "@/modules/team/subscribers/PlayerEnergyEventSubscriber";
import WageCalculator from "@/modules/team/services/WageCalculator";
import {NegotiationComponentManager} from "@/modules/diplomacy/services/NegotiationComponentManager";
import PlayerContractNegotiationComponent from "./components/PlayerContractNegotiation.vue";
import {TYPES} from "@/modules/diplomacy/types";
import {PlayerContractNegotiationCloser} from "@/modules/team/services/PlayerContractNegotiationCloser";
import {PlayerContractNegotiationStarter} from "@/modules/team/services/PlayerContractNegotiationStarter";
import {PlayerContractEventSubscriber} from "@/modules/team/subscribers/PlayerContractEventSubscriber";
import PlayerCareerEnder from "@/modules/team/services/PlayerCareerEnder";
import PlayerContractReleaser from "@/modules/team/services/PlayerContractReleaser";
import {interfaces} from "inversify";
import PlayerContractNegotiationContainer from "@/modules/team/services/PlayerContractNegotiationContainer";
import TerminationFeeCalculator from "@/modules/team/services/TerminationFeeCalculator";
import PlayerContractTerminator from "@/modules/team/services/PlayerContractTerminator";
import {SeasonEvaluationSubscriber} from "./subscribers/SeasonEvaluationSubscriber";
import {PlayerMarketValueEventSubscriber} from "@/modules/team/subscribers/PlayerMarketValueEventSubscriber";
import LineupValidator from "@/modules/team/services/LineupValidator";
import LineupHandler from "@/modules/team/services/LineupHandler";
import assets from "./resources/assets";
import {PlayerWageEventSubscriber} from "@/modules/team/subscribers/PlayerWageEventSubscriber";
import {PlayerSkillReductionEventSubscriber} from "@/modules/team/subscribers/PlayerSkillReductionEventSubscriber";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {PlayerContractExtender} from "@/modules/team/services/PlayerContractExtender";
import NameGenerator from "@/modules/team/services/NameGenerator";
import FriendlyGenerator from "./services/FriendlyGenerator";
import FastPlayerFactory from "@/modules/team/services/factories/FastPlayerFactory";
import TeamNameGenerator from "@/modules/team/services/TeamNameGenerator";
import CityRepository from "@/modules/team/services/CityRepository";
import TeamColorGenerator from "@/modules/team/services/TeamColorGenerator";

export class TeamModule extends Module {
  messages = messages;
  routes = routes;
  assets = assets;

  eventSubscribers = [
    GameCreationEventSubscriber,
    SeasonEvaluationSubscriber,
    PlayerProgressEventSubscriber,
    PlayerFormEventSubscriber,
    PlayerEnergyEventSubscriber,
    PlayerContractEventSubscriber,
    PlayerMarketValueEventSubscriber,
    PlayerWageEventSubscriber,
    PlayerSkillReductionEventSubscriber,
  ];

  public entities = [
    Team,
    Player,
    PlayerContractNegotiation,
    PlayerContractCareerEndNotification,
    PlayerContractTerminatedNotification,
  ];

  init() {
    super.init();

    this.app.bind<FastPlayerFactory>(FastPlayerFactory).to(FastPlayerFactory);
    this.app.bind<TeamFactory>(TeamFactory).to(TeamFactory);
    this.app.bind<LineupSkillCalculator>(LineupSkillCalculator).to(LineupSkillCalculator);
    this.app.bind<EffectiveLineupSkillCalculator>(EffectiveLineupSkillCalculator).to(EffectiveLineupSkillCalculator);
    this.app.bind<MarketValueCalculator>(MarketValueCalculator).to(MarketValueCalculator);
    this.app.bind<TerminationFeeCalculator>(TerminationFeeCalculator).to(TerminationFeeCalculator);
    this.app.bind<PlayerContractTerminator>(PlayerContractTerminator).to(PlayerContractTerminator);
    this.app.bind<LineupValidator>(LineupValidator).to(LineupValidator);
    this.app.bind<LineupHandler>(LineupHandler).to(LineupHandler);
    this.app.bind<NameGenerator>(NameGenerator).to(NameGenerator);
    this.app.bind<TeamNameGenerator>(TeamNameGenerator).to(TeamNameGenerator);
    this.app.bind<FriendlyGenerator>(FriendlyGenerator).to(FriendlyGenerator);
    this.app.bind<CityRepository>(CityRepository).to(CityRepository);
    this.app.bind<TeamColorGenerator>(TeamColorGenerator).to(TeamColorGenerator);

    this.app.bind<WageCalculator>(WageCalculator).to(WageCalculator);
    this
      .app
      .bind<interfaces.Factory<PlayerContractNegotiationContainer>>("Factory<PlayerContractNegotiationContainer>")
      .toFactory(context => (player: Player, years: number) => {
        return new PlayerContractNegotiationContainer(
          context.container.get(WageCalculator),
          context.container.get(ConfigRepository),
          player,
          years
        );
      });
    this.app.bind<PlayerCareerEnder>(PlayerCareerEnder).to(PlayerCareerEnder);
    this.app.bind<PlayerContractReleaser>(PlayerContractReleaser).to(PlayerContractReleaser);

    this.app.bind<PlayerContractNegotiationCloser>(TYPES.NegotiationCloser).to(PlayerContractNegotiationCloser);
    this.app.bind<PlayerContractNegotiationStarter>(PlayerContractNegotiationStarter).to(PlayerContractNegotiationStarter);
    this.app.bind<PlayerContractExtender>(PlayerContractExtender).to(PlayerContractExtender);
  }

  boot() {
    super.boot();

    this.app.get(LinkComponentManager).addItem({
      class: Team,
      component: TeamLink,
    });

    this.app.get(LinkComponentManager).addItem({
      class: Player,
      component: PlayerLink
    });

    this.app.get(NegotiationComponentManager).addItem({
      class: PlayerContractNegotiation,
      component: PlayerContractNegotiationComponent,
    });
  }
}